import { DeleteInvoiceDialog } from "@/components/invoices/DeleteInvoiceDialog";
import { Badge } from "@/components/ui/Badge";
import { ProviderIcon } from "@/components/ui/ProviderIcon";
import { useInvoices } from "@/contexts/InvoicesContext";
import { STATUS_BADGE_VARIANTS, styles } from "@/pages/InvoicesPage.styles";
import * as shared from "@/styles/shared";
import type { IconHintProps, InvoiceTableProps } from "@/types/ui";
import type { TranslateFunction } from "@shared/types/i18n";
import type { Invoice } from "@shared/types/models";
import { SortColumn } from "@shared/types/payment";
import { daysSince, formatAmount, formatDate, formatDayCount } from "@shared/utils";
import { Ban, Calendar, Eye, Pencil, Percent, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import * as invoiceTableStyles from "./InvoiceTable.styles";
import { SortArrow } from "./SortArrow";
import { StatusBadge } from "./StatusBadge";

const IconHint = ({ label, children }: IconHintProps) => (
  <span title={label} className="inline-flex">
    {children}
  </span>
);

const ageLabel = (invoice: Invoice, t: TranslateFunction) => {
  if (invoice.datePaid) return formatDate(invoice.datePaid);
  return formatDayCount(daysSince(invoice.invoiceDate), t);
};

const InvoiceTable = ({ invoices }: InvoiceTableProps) => {
  const { handleSort, handleDeleteInvoice } = useInvoices();
  const { t } = useTranslation(["common", "invoices", "invoiceDetail"]);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (pendingDelete === null) return;
    setDeleting(true);
    try {
      await handleDeleteInvoice(pendingDelete);
      setPendingDelete(null);
    } catch (error) {
      console.error("Failed to delete invoice:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className={invoiceTableStyles.mobileGrid}>
        {invoices.map((inv) => (
          <Link key={inv.id} to={`/invoices/${inv.id}`} className={`${styles.cardRow} hover:bg-accent`}>
            <div className={invoiceTableStyles.mobileCardHeader}>
              <span className={invoiceTableStyles.mobileTapText}>{t("tapToOpen", { ns: "invoices" })}</span>
              <svg className={invoiceTableStyles.iconH4W4TextMuted} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{t("detail.createdAt")}</span>
              <span className={shared.titleSmSemibold}>{formatDate(inv.createdAt)}</span>
            </div>
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{t("detail.date")}</span>
              <span className={styles.cardValue}>{formatDate(inv.invoiceDate)}</span>
            </div>
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{t("detail.address")}</span>
              <span className={styles.cardValue}>{inv.addressProvider.address.name}</span>
            </div>
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{t("detail.provider")}</span>
              <span className={invoiceTableStyles.providerValue}>
                <ProviderIcon provider={inv.addressProvider.provider.name} />
                {inv.addressProvider.provider.name}
              </span>
            </div>
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{t("detail.amount")}</span>
              <span className={shared.titleSmSemibold}>{formatAmount(inv.amount)}</span>
            </div>
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{inv.datePaid ? t("detail.datePaid") : t("detail.age")}</span>
              <span className={styles.cardValue}>{ageLabel(inv, t)}</span>
            </div>
            {inv.reference && (
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>{t("detail.reference")}</span>
                <span className={styles.cardValue}>{inv.reference}</span>
              </div>
            )}
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{t("detail.status")}</span>
              <StatusBadge status={inv.status} />
            </div>
            <div className={styles.cardField}>
              <span className={styles.cardLabel}>{t("detail.taxDeduction")}</span>
              <span className={styles.cardValue}>
                {inv.addressProvider.taxDeduction ? (
                  <Badge variant="info" size="sm">
                    <Percent className={invoiceTableStyles.badgeIcon} />
                    {t("enabled", { ns: "invoiceDetail" })}
                  </Badge>
                ) : (
                  <span className={shared.mutedBase}>{t("disabled", { ns: "invoiceDetail" })}</span>
                )}
              </span>
            </div>
            {inv.addressProvider.skipPayment && (
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>{t("detail.skipPayment")}</span>
                <span className={styles.cardValue}>
                  <Badge variant="warning" size="sm">
                    <Ban className={invoiceTableStyles.badgeIcon} />
                    {t("skipped", { ns: "invoiceDetail" })}
                  </Badge>
                </span>
              </div>
            )}
          </Link>
        ))}
      </div>

      <div className={styles.tableContainer}>
        <table className={`${styles.table} ${invoiceTableStyles.desktopTable}`}>
          <thead className={styles.thead}>
            <tr>
              <th className={`${styles.th} ${styles.sortable}`} onClick={() => handleSort(SortColumn.CREATED_AT)}>
                <span>{t("table.createdAt")}</span>
                <span className={styles.sortIcon}>
                  <SortArrow column={SortColumn.CREATED_AT} />
                </span>
              </th>
              <th className={`${styles.th} ${styles.sortable}`} onClick={() => handleSort(SortColumn.INVOICE_DATE)}>
                <span>{t("table.invoiceDate")}</span>
                <span className={styles.sortIcon}>
                  <SortArrow column={SortColumn.INVOICE_DATE} />
                </span>
              </th>
              <th className={styles.th}>{t("table.address")}</th>
              <th className={styles.th}>{t("table.provider")}</th>
              <th className={`${styles.th} ${styles.sortable}`} onClick={() => handleSort(SortColumn.AMOUNT)}>
                <span>{t("table.amount")}</span>
                <span className={styles.sortIcon}>
                  <SortArrow column={SortColumn.AMOUNT} />
                </span>
              </th>
              <th className={styles.th}>{t("table.datePaid")}</th>
              <th className={styles.th}>{t("table.status")}</th>
              <th className={styles.th}>{t("table.actions")}</th>
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {invoices.map((inv, idx) => (
              <tr key={inv.id} className={`${styles.row} ${idx % 2 === 0 ? styles.rowEven : ""}`}>
                <td className={`${styles.td} ${shared.titleSmSemibold}`}>
                  <span className={invoiceTableStyles.inlineFlexGap1_5}>
                    <Calendar className={invoiceTableStyles.iconH3_5W3_5TextMuted} />
                    {formatDate(inv.createdAt)}
                  </span>
                </td>
                <td className={`${styles.td} ${styles.tdPrimary}`}>
                  <span className={invoiceTableStyles.inlineFlexGap1_5}>
                    <Calendar className={invoiceTableStyles.iconH3_5W3_5TextMuted} />
                    {formatDate(inv.invoiceDate)}
                  </span>
                </td>
                <td className={`${styles.td} ${shared.mutedBase}`}>{inv.addressProvider.address.name}</td>
                <td className={`${styles.td} ${shared.mutedBase}`}>
                  <span className={invoiceTableStyles.inlineFlexGap1_5}>
                    <ProviderIcon provider={inv.addressProvider.provider.name} />
                    {inv.addressProvider.provider.name}
                  </span>
                </td>
                <td className={`${styles.td} ${styles.tdAmount}`}>{formatAmount(inv.amount)}</td>
                <td className={`${styles.td} ${shared.mutedBase}`}>
                  <span className={invoiceTableStyles.inlineFlexGap1_5}>
                    {inv.datePaid ? formatDate(inv.datePaid) : ageLabel(inv, t)}
                    {inv.addressProvider.taxDeduction && (
                      <IconHint label={t("detail.taxDeduction")}>
                        <Percent className={invoiceTableStyles.iconH3_5W3_5TextMuted} />
                      </IconHint>
                    )}
                    {inv.addressProvider.skipPayment && (
                      <IconHint label={t("detail.skipPayment")}>
                        <Ban className={invoiceTableStyles.iconH3_5W3_5TextMuted} />
                      </IconHint>
                    )}
                  </span>
                </td>
                <td className={styles.td}>
                  <Badge variant={STATUS_BADGE_VARIANTS[inv.status]}>{t(inv.status, { ns: "status" })}</Badge>
                </td>
                <td className={styles.td}>
                  <div className={invoiceTableStyles.inlineFlexGap0_5}>
                    <Link to={`/invoices/${inv.id}`} className={styles.actionBtn} title={t("view", { ns: "invoices" })}>
                      <Eye className={shared.iconSm} />
                    </Link>
                    <Link to={`/invoices/${inv.id}`} className={styles.actionBtn} title={t("edit", { ns: "invoices" })}>
                      <Pencil className={shared.iconSm} />
                    </Link>
                    <button className={styles.actionBtn} title={t("delete", { ns: "invoices" })} onClick={() => setPendingDelete(inv.id)}>
                      <Trash2 className={shared.iconSm} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeleteInvoiceDialog
        isOpen={pendingDelete !== null}
        deleting={deleting}
        title={t("deleteConfirmTitle", { ns: "invoices" })}
        message={t("deleteConfirmMessage", { ns: "invoices", id: pendingDelete ?? 0 })}
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </>
  );
};

export { InvoiceTable };
